import AdminTable from '../../../components/admin/AdminTable';
import React, { useState, useEffect, useCallback } from 'react';
import { Edit, Trash2, Plus, History, ShieldPlus } from 'lucide-react';
import { fetchConfiguracionesPago, ConfiguracionPago} from '../../../services/configuracionPagoService';
import AddConfigPagos from '../../../components/ConfigPagos/AddConfigPagos';
import EditConfigPagos from '../../../components/ConfigPagos/EditConfigPagos';
import DeleteConfigPagos from '../../../components/ConfigPagos/DeleteConfigPagos';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';
import Loading from '../../../components/Loading';
import { useNavigate } from 'react-router-dom';

const ConfigPagosPage: React.FC = () => {
    const [configuraciones, setConfiguraciones] = useState<ConfiguracionPago[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    
    // Estados para modales
    const [modalAbierto, setModalAbierto] = useState(false);
    const [modalEditarAbierto, setModalEditarAbierto] = useState(false);
    const [modalEliminarAbierto, setModalEliminarAbierto] = useState(false);
    const [configuracionEditando, setConfiguracionEditando] = useState<ConfiguracionPago | null>(null);
    const [configuracionEliminando, setConfiguracionEliminando] = useState<ConfiguracionPago | null>(null);

    // Paginación
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Sesión expirada
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const navigate = useNavigate();

    const userLevelId = user?.user_level_id ?? 0;
    // Ajusta el nombre del modelo según tu backend (p.ej. 'configuracionpago', 'configuracion_pago')
    const perm = useModelPermission('configuracionpago', userLevelId);

    // Función para cargar las configuraciones
    const cargarConfiguraciones = useCallback(async () => {
        setLoading(true);
        try {
            if (perm?.can_read) {
                const data = await fetchConfiguracionesPago();
                const dataOrdenada = [...data].sort((a, b) => a.id - b.id);
                setConfiguraciones(dataOrdenada);
                setError(null);
            } else {
                setConfiguraciones([]);
            }
        } catch (err) {
            setError('Error al cargar las configuraciones de pago');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [perm]);

    useEffect(() => {
        if (perm === null) return; // esperar a que los permisos se carguen
        cargarConfiguraciones();
    }, [perm, cargarConfiguraciones]);

    // Funciones para abrir modales
    const abrirModalEdicion = (config: ConfiguracionPago) => {
        setConfiguracionEditando(config);
        setModalEditarAbierto(true);
    };

    const abrirModalEliminacion = (config: ConfiguracionPago) => {
        setConfiguracionEliminando(config);
        setModalEliminarAbierto(true);
    };

    // Funciones de actualización optimista
    const manejarConfiguracionCreada = async (nuevaConfiguracion: ConfiguracionPago) => {
        try {
            setConfiguraciones(prev => [...prev, nuevaConfiguracion].sort((a, b) => a.id - b.id));
            setModalAbierto(false);
        } catch (err) {
            console.error('Error en actualización optimista:', err);
            cargarConfiguraciones();
        }
    };

    const manejarConfiguracionActualizada = async (configActualizada: ConfiguracionPago) => {
        try {
            setConfiguraciones(prev => 
                prev.map(config => 
                    config.id === configActualizada.id ? configActualizada : config
                ).sort((a, b) => a.id - b.id)
            );
            setModalEditarAbierto(false);
            setConfiguracionEditando(null);
        } catch (err) {
            console.error('Error en actualización optimista:', err);
            cargarConfiguraciones();
        }
    };

    const manejarConfiguracionEliminada = async (id: number) => {
        try {
            setConfiguraciones(prev => prev.filter(config => config.id !== id));
            setModalEliminarAbierto(false);
            setConfiguracionEliminando(null);
        } catch (err) {
            console.error('Error en actualización optimista:', err);
            cargarConfiguraciones();
        }
    };

    const totalPages = Math.ceil(configuraciones.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedConfiguraciones = configuraciones.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null || (loading && configuraciones.length === 0)) {
        return <Loading text="Cargando configuración de pagos..." />;
    }

    return (
        <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800">Configuración de Pagos</h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/config-pago" className="text-[#c1348b] hover:text-[#8f1f64]">Configuración de Pagos</a>
                    </div>
                </div>

                <div className="flex gap-2 items-center">
                    {perm.can_read && (
                        <button
                            style={{ height: '39px' }}
                            className="bg-[#61257b] text-white px-4 py-2 rounded-lg hover:bg-[#4a1c5e] flex items-center text-sm font-medium transition-colors"
                            onClick={() => navigate('/config-pago/history')} // Ajusta la ruta si existe
                        >
                            <History className="w-5 h-5" />
                        </button>
                    )}

                    {perm.can_create && (
                        <button
                            style={{ minWidth: '170.75px', height: '39px' }}
                            className="bg-[#c1348b] text-white px-4 py-2 rounded-lg hover:bg-[#8f1f64] flex items-center text-sm font-medium transition-colors"
                            onClick={() => setModalAbierto(true)}
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Agregar Configuración
                        </button>
                    )}
                </div>
            </div>

            {/* Modales */}
            <AddConfigPagos
                isOpen={modalAbierto}
                onClose={() => setModalAbierto(false)}
                onConfiguracionCreated={manejarConfiguracionCreada}
            />
            
            <EditConfigPagos
                isOpen={modalEditarAbierto}
                onClose={() => {
                    setModalEditarAbierto(false);
                    setConfiguracionEditando(null);
                }}
                onConfiguracionUpdated={manejarConfiguracionActualizada}
                configuracion={configuracionEditando}
            />
            
            <DeleteConfigPagos
                isOpen={modalEliminarAbierto}
                onClose={() => {
                    setModalEliminarAbierto(false);
                    setConfiguracionEliminando(null);
                }}
                onConfiguracionDeleted={manejarConfiguracionEliminada}
                configuracion={configuracionEliminando}
            />
            
            <ExpiredSessionModal
                isOpen={isSessionExpired}
                onClose={async () => {
                    setIsSessionExpired(false);
                    await logout();
                }}
            />

            {/* Tabla */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="text-center py-4">Cargando...</div>
                ) : error ? (
                    <div className="text-center py-4 text-red-500">{error}</div>
                ) : !perm.can_read ? (
                    <div className="text-center py-12">
                        <ShieldPlus className="w-12 h-12 text-gray-400 mx-auto" />
                        <p className="text-lg font-medium text-gray-600 mt-4">
                            No tienes permiso para ver las configuraciones de pago
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <AdminTable className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Afecta a caja</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requiere cambio</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Permite monto personalizado</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha de creación</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {displayedConfiguraciones.length > 0 ? (
                                    displayedConfiguraciones.map((config) => (
                                        <tr key={config.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-4 text-sm text-gray-900 font-medium">{config.nombre}</td>
                                            <td className="px-4 py-4 text-sm text-gray-500">
                                                {config.tipo === 'efectivo' && 'Efectivo'}
                                                {config.tipo === 'banco' && 'Banco'}
                                                {config.tipo === 'otro' && 'Otro'}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">
                                                {config.afecta_caja ? (
                                                    <span className="px-2 inline-flex text-xs font-medium rounded-full bg-green-100 text-green-800">Sí</span>
                                                ) : (
                                                    <span className="px-2 inline-flex text-xs font-medium rounded-full bg-gray-100 text-gray-800">No</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">
                                                {config.requiere_cambio ? (
                                                    <span className="px-2 inline-flex text-xs font-medium rounded-full bg-green-100 text-green-800">Sí</span>
                                                ) : (
                                                    <span className="px-2 inline-flex text-xs font-medium rounded-full bg-gray-100 text-gray-800">No</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">
                                                {config.permite_monto_personalizado ? (
                                                    <span className="px-2 inline-flex text-xs font-medium rounded-full bg-green-100 text-green-800">Sí</span>
                                                ) : (
                                                    <span className="px-2 inline-flex text-xs font-medium rounded-full bg-gray-100 text-gray-800">No</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">
                                                {new Date(config.fecha_creacion).toLocaleDateString('es-ES', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    hour12: false,
                                                })}
                                            </td>
                                            <td className="px-4 py-4">
                                                {config.activo ? (
                                                    <span className="px-2 inline-flex text-xs font-medium rounded-full bg-green-100 text-green-800">Activo</span>
                                                ) : (
                                                    <span className="px-2 inline-flex text-xs font-medium rounded-full bg-red-100 text-red-800">Inactivo</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex gap-2">
                                                    {perm.can_update && (
                                                        <button
                                                            onClick={() => abrirModalEdicion(config)}
                                                            className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-[#c1348b] hover:text-[#8f1f64] transition"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    {perm.can_delete && (
                                                        <button
                                                            onClick={() => abrirModalEliminacion(config)}
                                                            className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-800 transition"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={8} className="px-6 py-12 text-center">
                                            <div className="flex flex-col items-center justify-center space-y-3">
                                                <ShieldPlus className="w-12 h-12 text-gray-400" />
                                                <span className="text-lg font-medium text-gray-600">
                                                    No hay configuraciones de pago disponibles
                                                </span>
                                                {perm.can_create && (
                                                    <p className="text-gray-500 text-sm">
                                                        Agrega una nueva configuración para comenzar
                                                    </p>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </AdminTable>
                    </div>
                )}

                {/* Paginación */}
                {perm.can_read && !loading && configuraciones.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={configuraciones.length}
                    />
                )}
            </div>
        </div>
    );
};

export default ConfigPagosPage;
